import type { IPricingItemBody } from '../../services/pricingService';

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const checked = (value: unknown) => value === true || value === 'true';

export const CALCULATION_METHODS = [
  { key: 'UnitRate', value: 'Unit rate' },
  { key: 'Percentage', value: 'Percentage' },
] as const;

export const CHARGE_ROLES = [
  { key: 'BaseFee', value: 'Base fee' },
  { key: 'AdditionalCharge', value: 'Additional charge' },
] as const;

export const PERCENTAGE_BASES = [
  { key: 'ReportBaseFee', value: 'Report base fee' },
  { key: 'BasePlusEligibleExtras', value: 'Base fee plus eligible extras' },
] as const;

export const PERCENTAGE_BASIS_HINT =
  'Percentage surcharges use either the report base fee or the base fee plus eligible extras.';

export const PERCENTAGE_BASE_ELIGIBLE_HINT =
  'Only a unit-rate additional charge can be included in the base fee plus eligible extras.';

const labelOf = (
  options: readonly { key: string; value: string }[],
  key: string | null | undefined
) => options.find((option) => option.key === key)?.value ?? '';

const isKey = (options: readonly { key: string }[], value: string) =>
  options.some((option) => option.key === value);

export const calculationMethodLabel = (key: string | null | undefined) =>
  labelOf(CALCULATION_METHODS, key);

export const chargeRoleLabel = (key: string | null | undefined) =>
  labelOf(CHARGE_ROLES, key);

export const percentageBasisLabel = (key: string | null | undefined) =>
  labelOf(PERCENTAGE_BASES, key);

export type PricingItemBodyResult =
  | { ok: true; body: IPricingItemBody }
  | { ok: false; error: string };

export const pricingItemBody = (
  item: Record<string, unknown>
): PricingItemBodyResult => {
  const code = text(item.code);
  const name = text(item.name);
  const type = text(item.type);
  if (!code || !name || !type) {
    return { ok: false, error: 'Code, name, and type are required.' };
  }
  const calculationMethod = text(item.calculationMethod);
  const percentage = calculationMethod === 'Percentage';
  const chargeRole = percentage ? 'AdditionalCharge' : text(item.chargeRole);
  if (!isKey(CALCULATION_METHODS, calculationMethod)) {
    return { ok: false, error: 'Calculation method is required.' };
  }
  if (!isKey(CHARGE_ROLES, chargeRole)) {
    return { ok: false, error: 'Charge role is required.' };
  }
  const quantityType = percentage ? 'Fixed' : text(item.quantityType);
  if (quantityType !== 'Fixed' && quantityType !== 'AssetCount') {
    return { ok: false, error: 'Quantity type is required.' };
  }
  const percentageBasis = text(item.percentageBasis);
  if (percentage && !isKey(PERCENTAGE_BASES, percentageBasis)) {
    return { ok: false, error: 'Percentage items require a percentage basis.' };
  }
  const description = text(item.description);
  const timeServiceId = Number(item.timeServiceId);
  return {
    ok: true,
    body: {
      code,
      name,
      description: description || null,
      type,
      isRequired: checked(item.isRequired),
      quantityType,
      calculationMethod,
      chargeRole,
      percentageBasis: percentage ? percentageBasis : null,
      isPercentageBaseEligible:
        !percentage &&
        chargeRole === 'AdditionalCharge' &&
        checked(item.isPercentageBaseEligible),
      exclusivityGroup: text(item.exclusivityGroup) || null,
      timeServiceId:
        type === 'Service' &&
        Number.isInteger(timeServiceId) &&
        timeServiceId > 0
          ? timeServiceId
          : null,
    },
  };
};
